import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CreditCard,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  FileText,
  Users,
  User,
  Banknote,
  Shield,
} from "lucide-react";
import { bondsAPI, subscriptionAPI, paymentsAPI, redemptionPaymentsAPI, subAgreementRequestsAPI } from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { useAuth } from "../../contexts/AuthContext";
import { BOND_STATUS } from "../../constants/subscription.constants";

const FacilityAgentDashboard = () => {
  const { getDisplayName } = useAuth();

  // Fetch bonds data for real upcoming maturities count
  const { data: bondsResponse, isLoading: bondsLoading, error: bondsError } = useQuery({
    queryKey: ['bonds', 'facility-agent-dashboard'],
    queryFn: () => bondsAPI.getBonds({ limit: 100 }),
    select: (response) => {
      const bonds = response?.data?.data?.bonds || [];
      return bonds;
    }
  });

  // Fetch bonds pending approval (Bond Applications)
  const { data: pendingBondsResponse, isLoading: pendingBondsLoading, error: pendingBondsError } = useQuery({
    queryKey: ['bonds', 'pending-approval', 'facility-agent-dashboard'],
    queryFn: () => bondsAPI.getBonds({
      status: BOND_STATUS.PENDING_REVIEW,
      limit: 100
    }),
    select: (response) => {
      console.log('Debug - Pending Bonds API Response:', response);
      const bonds = response?.data?.data?.bonds || [];
      console.log('Debug - Extracted pending bonds:', bonds);
      console.log('Debug - Total pending bonds before filter:', bonds.length);

      // Only count bonds that have subscription window set (same logic as BondApplications page)
      const filteredBonds = bonds.filter(
        (bond) =>
          (bond.subscriptionStart || bond.subscriptionStartDate) &&
          (bond.subscriptionEnd || bond.subscriptionEndDate)
      );

      console.log('Debug - Pending bonds after subscription window filter:', filteredBonds.length);
      console.log('Debug - Filtered bonds:', filteredBonds.map(b => ({
        name: b.bondName,
        subscriptionStart: b.subscriptionStart || b.subscriptionStartDate,
        subscriptionEnd: b.subscriptionEnd || b.subscriptionEndDate
      })));

      return filteredBonds;
    }
  });

  // Fetch pending sub-agreement approval count from backend
  const { data: subAgreementCountResponse, isLoading: subAgreementCountLoading, error: subAgreementCountError } = useQuery({
    queryKey: ['sub-agreement-pending-count', 'facility-agent-dashboard'],
    queryFn: () => subAgreementRequestsAPI.getPendingApprovalCount(),
    select: (response) => {
      console.log('Debug - Sub-Agreement Count API Response:', response);
      const count = response?.data?.data?.count || 0;
      console.log('Debug - Pending sub-agreement count:', count);
      return count;
    }
  });

  // Calculate active subscriptions (bonds still within subscription period)
  const calculateActiveSubscriptions = (bonds) => {
    // Ensure bonds is an array
    if (!Array.isArray(bonds) || bonds.length === 0) {
      return 0;
    }

    const currentDate = new Date();

    // Filter bonds that are within subscription period
    const activeSubscriptionBonds = bonds.filter(bond => {
      // Check if bond has subscription start and end dates
      if (!bond.subscriptionStart || !bond.subscriptionEnd) {
        return false;
      }

      const subscriptionStart = new Date(bond.subscriptionStart);
      const subscriptionEnd = new Date(bond.subscriptionEnd);

      // Bond is in active subscription if current date is between start and end
      const isActive = currentDate >= subscriptionStart && currentDate <= subscriptionEnd;

      return isActive;
    });

    console.log(`Total active subscriptions: ${activeSubscriptionBonds.length}`);

    return activeSubscriptionBonds.length;
  };

  // Ensure we have arrays before calculating
  const bonds = Array.isArray(bondsResponse) ? bondsResponse : [];
  const pendingBonds = Array.isArray(pendingBondsResponse) ? pendingBondsResponse : [];
  const subAgreementApprovalCount = subAgreementCountResponse || 0;

  console.log('Debug - Dashboard data:', {
    bonds: bonds.length,
    pendingBonds: pendingBonds.length,
    subAgreementApprovalCount: subAgreementApprovalCount
  });

  const activeSubscriptionsCount = bondsError ? 0 : calculateActiveSubscriptions(bonds);
  const pendingApprovalCount = pendingBondsError ? 0 : pendingBonds.length;

  // All metrics now use real data from APIs
  const dashboardMetrics = {
    activeSubscriptions: activeSubscriptionsCount,
    pendingApproval: pendingApprovalCount,
    subAgreementApproval: subAgreementApprovalCount,
  };




  // Show loading spinner while fetching data
  if (bondsLoading || pendingBondsLoading || subAgreementCountLoading) {
    return <LoadingSpinner />;
  }

  // Handle errors if API calls failed
  if (bondsError) {
    console.error('Bonds API error:', bondsError);
  }

  if (pendingBondsError) {
    console.error('Pending Bonds API error:', pendingBondsError);
  }

  if (subAgreementCountError) {
    console.error('Sub-Agreement Count API error:', subAgreementCountError);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Welcome, {getDisplayName()}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Facility Agent Dashboard - Manage bond
                lifecycle operations
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Key Metrics */}
        <div className="mb-8">
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-medium text-dark-gray">
                Key Metrics
              </h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <Link
                  to="/facility-agent/subscription"
                  className="p-6 transition-shadow bg-white rounded-lg shadow hover:shadow-md"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0 p-3 bg-purple-100 rounded-lg">
                      <Calendar className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0 ml-4">
                      <p className="text-sm font-medium text-gray-600">
                        Active Subscriptions
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {dashboardMetrics.activeSubscriptions}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Bonds within subscription period
                      </p>
                    </div>
                  </div>
                </Link>

                <Link
                  to="/facility-agent/applications"
                  className="p-6 transition-shadow bg-white rounded-lg shadow hover:shadow-md"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0 p-3 bg-orange-100 rounded-lg">
                      <Clock className="w-6 h-6 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0 ml-4">
                      <p className="text-sm font-medium text-gray-600">
                        Pending Approval
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {dashboardMetrics.pendingApproval}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Bond applications awaiting review
                      </p>
                    </div>
                  </div>
                </Link>

                <Link
                  to="/facility-agent/sub-agreement-management"
                  className="p-6 transition-shadow bg-white rounded-lg shadow hover:shadow-md"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0 p-3 bg-blue-100 rounded-lg">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0 ml-4">
                      <p className="text-sm font-medium text-gray-600">
                        Subagreement Approval
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {dashboardMetrics.subAgreementApproval}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Subgreements awaiting review
                      </p>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-medium text-dark-gray">
                Quick Actions
              </h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-3 gap-3">
                <Link
                  to="/facility-agent/applications"
                  className="w-full flex flex-col items-center justify-center p-4 text-sm font-medium rounded border btn-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                  <FileText
                    className="w-6 h-6 mb-2"
                    style={{ color: "var(--color-primary)" }}
                  />
                  Bond Applications
                </Link>

                <Link
                  to="/facility-agent/sub-agreement-management"
                  className="w-full flex flex-col items-center justify-center p-4 text-sm font-medium rounded border btn-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                  <CheckCircle
                    className="w-6 h-6 mb-2"
                    style={{ color: "var(--color-primary)" }}
                  />
                  Subagreement Management
                </Link>

                <Link
                  to="/facility-agent/subscription"
                  className="w-full flex flex-col items-center justify-center p-4 text-sm font-medium rounded border btn-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2"
                >
                  <Calendar
                    className="w-6 h-6 mb-2"
                    style={{ color: "var(--color-primary)" }}
                  />
                  Manage Subscriptions
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links removed per request */}


        {/* System Alerts removed per request */}
      </div>
    </div>
  );
};

export default FacilityAgentDashboard;
